<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';

const route = useRoute();
const router = useRouter();

const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
  },
});

const currentPage = ref(Number(route.query.page) || 1);

watch(
  () => route.query.page,
  (val) => {
    currentPage.value = Number(val) || 1;
  }
);

const goToPage = (page) => {
  if (page < 1 || page > props.totalPages) return;
  router.push({
    query: {
      ...route.query,
      page,
    },
  });
};

const pages = computed(() => {
  const total = props.totalPages;
  const current = currentPage.value;
  const maxVisible = 3;
  const pages = [];

  pages.push(1);

  let start = Math.max(2, current - Math.floor(maxVisible / 2));
  let end = Math.min(total - 1, current + Math.floor(maxVisible / 2));

  if (current <= Math.floor(maxVisible / 2)) {
    end = Math.min(total - 1, maxVisible);
  }
  if (current > total - Math.floor(maxVisible / 2)) {
    start = Math.max(2, total - maxVisible + 1);
  }

  if (start > 2) {
    pages.push('...');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < total - 1) {
    pages.push('...');
  }

  if (total > 1) {
    pages.push(total);
  }

  return pages;
});

watch(
  () => route.query.page,
  (val) => {
    let page = Number(val) || 1;

    if (page < 1) page = 1;
    if (page > props.totalPages) page = props.totalPages;

    currentPage.value = page;

    if (page !== Number(val)) {
      router.replace({
        query: {
          ...route.query,
          page,
        },
      });
    }
  },
  { immediate: true }
);

</script>

<template>
  <section class="base-pagination">
    <div class="row small-regular">
      <ul class="pagination-items">
        <li
          class="page-item rounded-l-lg flex-1 cursor-pointer"
          :class="{ 'opacity-50 pointer-events-none': currentPage === 1 }"
          @click="goToPage(currentPage - 1)"
        >
          <ChevronLeftIcon class="icon" />
        </li>

        <li
          v-for="(page, idx) in pages"
          :key="idx"
          class="page-item cursor-pointer"
          :class="{
            selected: page === currentPage,
            'pointer-events-none': page === '...',
          }"
          @click="page !== '...' && goToPage(page)"
        >
          {{ page }}
        </li>

        <li
          class="page-item rounded-r-lg flex-1 cursor-pointer"
          :class="{ 'opacity-50 pointer-events-none': currentPage === totalPages }"
          @click="goToPage(currentPage + 1)"
        >
          <ChevronRightIcon class="icon" />
        </li>
      </ul>
    </div>
  </section>
</template>


<style lang="postcss" scoped>
.base-pagination {
  .row {
    @apply md:flex items-center justify-between text-center md:text-left;
    .showing-classes {
      @apply text-secondary-text mb-3 md:mb-0;
    }
    .pagination-items {
      @apply inline-flex items-stretch rounded-lg border border-gray-200;
      .page-item {
        @apply px-3 py-1.5 transition text-secondary-text md:hover:bg-accent-1
        md:hover:bg-opacity-5 cursor-pointer font-medium flex items-center
        justify-center;
        &:not(:last-of-type) {
          @apply border-r;
        }
        &.selected {
          @apply text-accent-1 bg-accent-1 bg-opacity-5;
        }
        .icon {
          @apply w-4 h-4;
        }
      }
    }
  }
}
</style>
